<script>
import ProductCard from '@phoenix/mixins/components/product/ProductCard';
import ProductCardTags from '@maison/components/product/ProductCardTags';
import WishlistButton from '../wishlist/WishlistButton';
import { PhxsgButton } from '@sg/src/View/Components/Atoms/Button';

export default {
  components: {
    ProductCardTags,
    WishlistButton,
    PhxsgButton,
  },

  mixins: [ProductCard],

  props: {
    reference: {
      type: String,
      required: true,
    },
    referenceText: {
      type: String,
      default: '',
    },
    internalReference: {
      type: String,
      default: '',
    },
    labels: {
      type: Array,
      default: () => [],
    },
    labelsNamedPagesUrls: {
      type: Object,
      default: () => ({}),
    },
    horizontal: {
      type: Boolean,
      default: true,
    },
    tight: {
      type: Boolean,
      default: false,
    },
    showReference: {
      type: Boolean,
      default: true,
    },
    showTags: {
      type: Boolean,
      default: true,
    },
    showVat: {
      type: Boolean,
      default: true,
    },
    lazy: {
      type: Boolean,
      default: true,
    },
    shouldDisplayBuyNow: {
      type: Boolean,
      default: false,
    },
    defaultInWishlistIcon: {
      type: String,
      default: 'heart-full',
    },
    smallPrice: {
      type: Boolean,
      default: false,
    },
    shouldDisplayPrice: {
      type: Boolean,
      default: true,
    },
    subtitle: {
      type: String,
      default: '',
    },
    pictureFull: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    classes() {
      return [
        'product-card',
        {
          'product-card--horizontal-tight': this.tight,
          'product-card--horizontal': this.horizontal,
        },
      ];
    },

    priceClasses() {
      return ['product-price', { 'product-price--small': this.smallPrice }];
    },

    pictureClasses() {
      return { 'picture--contain': !this.pictureFull, 'picture--full': this.pictureFull };
    },
  },
};
</script>

<template>
  <div :class="classes" :data-cy-reference="reference">
    <div class="picture product-card__picture" :class="pictureClasses">
      <picture-lazy :lazy="lazy" v-bind="image" />
    </div>
    <div class="product-card__description">
      <a :href="url" :target="openLinkInNewWindow ? '_blank' : '_self'" class="product-card__link expand-target">
        <div class="product-card__name">
          <span dir="ltr">{{ name }}</span>
        </div>
        <div v-if="subtitle" class="product-card__collection">{{ subtitle }}</div>
        <div v-if="showReference" class="product-card__ref" data-cy="product-card-reference" v-html="referenceText" />

        <div v-if="shouldDisplayPrice" :class="priceClasses">
          <span class="product-price__value" data-cy="product-price-value">
            <slot name="price"></slot>
          </span>
          <span v-show="showVat" class="product-price__vat">{{ vatInformation }}</span>
        </div>

        <slot name="extra-fees"></slot>

        <div
          v-for="error in errors"
          :key="`error_${id}_${error}`"
          class="alert alert--badge mt-xs alert--secondary"
          role="alert"
          data-cy="no-stock-alert-item"
        >
          <div class="alert__icon">
            <phx-icon icon="info" />
          </div>
          <div>
            {{ $t(`Checkout.product.${error}.tag`) }}
          </div>
        </div>
      </a>

      <product-card-tags
        :labels="labels"
        :labels-named-pages-urls="labelsNamedPagesUrls"
        :should-display="showTags"
        :should-align-start="horizontal"
      />

      <phxsg-button v-if="shouldDisplayBuyNow" :href="url" use="a" variant="full" class="mt-sm">
        {{ $t('Product.buyNow') }}
      </phxsg-button>
    </div>

    <ul role="presentation" class="product-card__actions">
      <li v-if="shouldDisplayRemoveFromCart" role="presentation">
        <phxsg-button
          icon-only
          :icon="{ start: { id: 'close' } }"
          :data-cy="dataCyRemoveButton"
          :label="$t('Checkout.removeProduct', { product: name })"
          @click.stop="onRemove({ id, tracking })"
        />
      </li>

      <li v-if="shouldDisplayAddToWishlist" role="presentation">
        <wishlist-button
          :reference="reference"
          :internal-reference="internalReference"
          :name="name"
          :default-in-wishlist-icon="defaultInWishlistIcon"
        />
      </li>
    </ul>
  </div>
</template>
